// 적립금 화면.
// 작업계획서 4.6: 마이페이지에서 적립금 확인. 잔액 + 적립/사용 내역을 보여준다.
//  - 적립은 주문 수령완료 시 확정된다 (현장 결제 모델).

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  POINT_TX_TYPE,
  formatPoints,
  type PointTransactionDto,
} from "@oo/shared";
import { getPoints } from "@/features/points/pointsApi.ts";

// 적립금 내역 화면.
export default function PointsPage() {
  const pointsQuery = useQuery({ queryKey: ["points"], queryFn: getPoints });

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-28">
      <h1 className="text-2xl font-bold">적립금</h1>

      {/* 잔액 카드 */}
      <section className="rounded-lg border border-ink-200 bg-brand-600 p-5 text-white">
        <p className="text-sm text-ink-300">사용 가능 적립금</p>
        <p className="mt-1 text-3xl font-bold tabular-nums">
          {pointsQuery.isLoading ? "..." : formatPoints(pointsQuery.data?.balance ?? 0)}
        </p>
        <p className="mt-2 text-xs text-ink-600">
          적립금은 주문을 수령 완료하면 적립됩니다.
        </p>
      </section>

      {/* 내역 */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">적립/사용 내역</h2>
        {pointsQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-ink-100" />
            ))}
          </div>
        ) : (pointsQuery.data?.transactions.length ?? 0) === 0 ? (
          <div className="rounded-lg border border-dashed border-ink-300 p-8 text-center text-sm text-ink-600">
            아직 적립 내역이 없습니다.
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {pointsQuery.data?.transactions.map((tx) => (
              <TxRow key={tx.id} tx={tx} />
            ))}
          </ul>
        )}
      </section>

      <Link to="/me" className="text-center text-sm text-ink-600 underline">
        마이페이지로
      </Link>
    </main>
  );
}

// 내역 한 줄. 적립(+)은 강조색, 사용/취소(-)는 중립색.
function TxRow({ tx }: { tx: PointTransactionDto }) {
  const isEarn = tx.type === POINT_TX_TYPE.EARN || tx.amount > 0;
  const sign = tx.amount > 0 ? "+" : "";
  // 적립/사용 외(관리자 지급·환불·게임 보상 등 adjust)는 손님 화면에 '지급'으로 통일 표기.
  const title =
    tx.type === POINT_TX_TYPE.EARN
      ? "적립"
      : tx.type === POINT_TX_TYPE.SPEND
        ? "사용"
        : "지급";
  // 'YYYY-MM-DDTHH:MM:SS...' -> 'YYYY.MM.DD'
  const dateLabel = tx.createdAt.slice(0, 10).replace(/-/gu, ".");
  return (
    <li className="flex items-center justify-between rounded-lg border border-ink-200 bg-white px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {title}
          {tx.orderNo ? <span className="text-ink-600"> · 주문 {tx.orderNo}</span> : null}
        </p>
        <p className="text-xs text-ink-600">
          {dateLabel}
          {tx.reason ? ` · ${tx.reason}` : ""}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className={`text-base font-bold tabular-nums ${isEarn ? "text-ink-900" : "text-ink-600"}`}>
          {sign}
          {formatPoints(Math.abs(tx.amount))}
        </p>
        <p className="text-[11px] text-ink-600 tabular-nums">잔액 {formatPoints(tx.balanceAfter)}</p>
      </div>
    </li>
  );
}
